import { message } from "antd";
import axiosInstance from "../../../helpers/axios";
import { responseDataFormate } from "../../../helpers/bycrypt";

export interface RolesFilterParams {
  id?: string;
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string | null;
  sortOrder?: string | null;
  //For getting specific roles on specific pages
  role?: string;
}

interface GetRolesListParams {
  filters: RolesFilterParams;
  onSuccess?: (data: any) => void;
  onError?: () => void;
}

export const getRolesListAPI =
  ({ filters, onSuccess, onError }: GetRolesListParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.get("/roles", {
        params: filters,
      });

      let result = responseDataFormate(response.data);

      if (result?.success) {
        onSuccess?.(result.data);
      } else {
        message.error(result?.message || "Failed to fetch Roles");
        onError?.();
      }
    } catch (error: any) {
      console.error("Get Roles List Error:", error);
      message.error(error?.response?.data?.message || "Unable to fetch Roles");
      onError?.();
    }
  };

export interface ClientsFilterParams {
  search?: string;
  page?: number;
  limit?: number;
  id?: string;
  sortBy?: string | null;
  sortOrder?: string | null;
  is_active?: number;
  created_from?: string;
  created_to?: string;
  created_by?: string;
  sales_person?: string;
  customer_status?: number;
}

interface GetClientsListParams {
  filters: ClientsFilterParams;
  onSuccess?: (data: any) => void;
  onError?: () => void;
  signal?: AbortSignal;
}

export const fetchClientsQueryFn = async (
  filters: any,
  signal?: AbortSignal,
) => {
  const response = await axiosInstance.get("/customers", {
    params: filters,
    signal,
  });
  const result = responseDataFormate(response.data);
  if (result?.success) {
    return result.data;
  }
  return { data: [], total: 0 };
};

export const getClientsListAPI =
  ({ filters, onSuccess, onError, signal }: GetClientsListParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.get("/customers", {
        params: filters,
        signal,
      });

      let result = responseDataFormate(response.data);

      if (result?.success) {
        onSuccess?.(result.data);
      } else {
        message.error(result?.message || "Failed to fetch Clients");
        onError?.();
      }
    } catch (error: any) {
      if (error.code === "ERR_CANCELED" || error.name === "AbortError") return;
      console.error("Get Clients List Error:", error);
      message.error(
        error?.response?.data?.message || "Unable to fetch Clients",
      );
      onError?.();
    }
  };

export const getCombinedClientsListAPI =
  ({ filters, onSuccess, onError, signal }: GetClientsListParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.get("/quote-requests/customers/search", {
        params: filters,
        signal,
      });

      let result = responseDataFormate(response.data);

      if (result?.success) {
        onSuccess?.(result.data);
      } else {
        message.error(result?.message || "Failed to fetch Clients");
        onError?.();
      }
    } catch (error: any) {
      if (error.code === "ERR_CANCELED" || error.name === "AbortError") return;
      console.error("Get Combined Clients List Error:", error);
      message.error(
        error?.response?.data?.message || "Unable to fetch Clients",
      );
      onError?.();
    }
  };

interface DeleteClientParams {
  id: string;
  params?: any;
  onSuccess: () => void;
  onError: () => void;
}

export const deleteClientAPI =
  ({ id, params, onSuccess, onError }: DeleteClientParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.delete(`/customers`, {
        data: { id: id },
        params,
      });

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message || "Client deleted successfully");
        onSuccess?.();
      } else {
        message.error(result?.message || "Failed to delete Client");
        onError?.();
      }
    } catch (error: any) {
      console.error("Delete Client error:", error);
      onError?.();

      const errorMessage =
        error?.response?.data?.message || "Unable to delete Client";
      message.error(errorMessage);
    }
  };

interface UpdateClientParams {
  payload: any;
  params?: any;
  onSuccess: (data: any) => void;
  onError: () => void;
}

export const updateClientAPI =
  ({ payload, params, onSuccess, onError }: UpdateClientParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.put(`/customers`, payload, { params });

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message || "Client updated successfully");
        onSuccess?.(result.data);
      } else {
        message.error(result?.message || "Failed to update Client");
        onError?.();
      }
    } catch (error: any) {
      console.error("Update Client error:", error);
      onError?.();

      const errorMessage =
        error?.response?.data?.message || "Unable to update Client";
      message.error(errorMessage);
    }
  };

interface CreatePaymentMilestoneParams {
  payload: any;
  params?: any;
  onSuccess: (data: any) => void;
  onError: () => void;
}

export const createPaymentMilestioneAPI =
  ({ payload, params, onSuccess, onError }: CreatePaymentMilestoneParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.post(
        `/customers/payment-milestones`,
        payload,
        { params },
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(
          result?.message || "Payment Milestone created successfully",
        );
        onSuccess?.(result.data);
      } else {
        message.error(result?.message || "Failed to create Payment Milestone");
        onError?.();
      }
    } catch (error: any) {
      console.error("Create Payment Milestone error:", error);
      onError?.();

      const errorMessage =
        error?.response?.data?.message || "Unable to create Payment Milestone";
      message.error(errorMessage);
    }
  };

export interface PaymentMilestoneFilterParams {
  customer_id: string;
  page?: number;
  limit?: number;
}

interface GetPaymentMilestoneListParams {
  filters: PaymentMilestoneFilterParams;
  onSuccess?: (data: any) => void;
  onError?: () => void;
}

export const getPaymentMilestonesListAPI =
  ({ filters, onSuccess, onError }: GetPaymentMilestoneListParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.get(
        "/customers/payment-milestones",
        {
          params: filters,
        },
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        onSuccess?.(result.data);
      } else {
        message.error(
          result?.message || "Failed to fetch Client Payment Milestones",
        );
        onError?.();
      }
    } catch (error: any) {
      console.error("Get Client Payment Milestones List Error:", error);
      message.error(
        error?.response?.data?.message ||
          "Unable to fetch Client Payment Milestones",
      );
      onError?.();
    }
  };

interface UpdatePaymentMilestoneParams {
  payload: any;
  params?: any;
  onSuccess: (data: any) => void;
  onError: () => void;
}

export const updateClientPaymentMilestoneAPI =
  ({ payload, params, onSuccess, onError }: UpdatePaymentMilestoneParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.put(
        `/customers/payment-milestones`,
        payload,
        { params },
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(
          result?.message || "Payment Milestone updated successfully",
        );
        onSuccess?.(result.data);
      } else {
        message.error(result?.message || "Failed to update Payment Milestone");
        onError?.();
      }
    } catch (error: any) {
      console.error("Update Payment Milestone error:", error);
      onError?.();

      const errorMessage =
        error?.response?.data?.message || "Unable to update Payment Milestone ";
      message.error(errorMessage);
    }
  };

interface DeleteClientPaymentMilestoneParams {
  id: string;
  params?: any;
  onSuccess: () => void;
  onError: () => void;
}

export const deleteClientPaymentMilestoneAPI =
  ({ id, params, onSuccess, onError }: DeleteClientPaymentMilestoneParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.delete(
        `/customers/payment-milestones`,
        {
          data: { _id: id },
          params,
        },
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(
          result?.message || "Payment Milestone deleted successfully",
        );
        onSuccess?.();
      } else {
        message.error(result?.message || "Failed to delete Payment Milestone");
        onError?.();
      }
    } catch (error: any) {
      console.error("Delete Payment Milestone error:", error);
      onError?.();

      const errorMessage =
        error?.response?.data?.message || "Unable to delete Payment Milestone";
      message.error(errorMessage);
    }
  };

export interface ClientBillingAddressFilterParams {
  customer_id: string;
  page?: number;
  limit?: number;
}

interface GetClientsBillingAddressListParams {
  filters: ClientBillingAddressFilterParams;
  onSuccess?: (data: any) => void;
  onError?: () => void;
}

export const getClientBillingAddressListAPI =
  ({ filters, onSuccess, onError }: GetClientsBillingAddressListParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.get("/customers/billing-addresses", {
        params: filters,
      });

      let result = responseDataFormate(response.data);

      if (result?.success) {
        onSuccess?.(result.data);
      } else {
        message.error(
          result?.message || "Failed to fetch Clients Billing Address",
        );
        onError?.();
      }
    } catch (error: any) {
      console.error("Get Clients Billing Address List Error:", error);
      message.error(
        error?.response?.data?.message ||
          "Unable to fetch Clients Billing Address",
      );
      onError?.();
    }
  };

interface CreateBillingAddressParams {
  payload: any;
  params?: any;
  onSuccess: (data: any) => void;
  onError: () => void;
}

export const createClientBillingAddressAPI =
  ({ payload, params, onSuccess, onError }: CreateBillingAddressParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.post(
        `/customers/billing-addresses`,
        payload,
        { params },
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(
          result?.message || "Billing Address created successfully",
        );
        onSuccess?.(result.data);
      } else {
        message.error(result?.message || "Failed to create Billing Address");
        onError?.();
      }
    } catch (error: any) {
      console.error("Create Billing Address error:", error);
      onError?.();

      const errorMessage =
        error?.response?.data?.message || "Unable to create Billing Address";
      message.error(errorMessage);
    }
  };

interface UpdateClientBillingAddressParams {
  payload: any;
  params?: any;
  onSuccess: (data: any) => void;
  onError: () => void;
}

export const updateClientBillingAddressAPI =
  ({ payload, params, onSuccess, onError }: UpdateClientBillingAddressParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.put(
        `/customers/billing-addresses`,
        payload,
        { params },
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(
          result?.message || "Billing Address updated successfully",
        );
        onSuccess?.(result.data);
      } else {
        message.error(result?.message || "Failed to update Billing Address");
        onError?.();
      }
    } catch (error: any) {
      console.error("Update Billing Address error:", error);
      onError?.();

      const errorMessage =
        error?.response?.data?.message || "Unable to update Billing Address";
      message.error(errorMessage);
    }
  };

interface DeleteClientBillingAddressParams {
  id: string;
  params?: any;
  onSuccess: () => void;
  onError: () => void;
}

export const deleteClientBillingAddressAPI =
  ({ id, params, onSuccess, onError }: DeleteClientBillingAddressParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.delete(
        `/customers/billing-addresses`,
        {
          data: { id: id },
          params,
        },
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(
          result?.message || "Billing Address deleted successfully",
        );
        onSuccess?.();
      } else {
        message.error(result?.message || "Failed to delete Billing Address");
        onError?.();
      }
    } catch (error: any) {
      console.error("Delete Billing Address error:", error);
      onError?.();

      const errorMessage =
        error?.response?.data?.message || "Unable to delete Billing Address";
      message.error(errorMessage);
    }
  };

// --- Sage Country APIs ---

export interface GetSageCountriesParams {
  params?: any;
  onSuccess?: (data: any) => void;
  onError?: () => void;
}

export const getSageCountriesAPI =
  ({ params, onSuccess, onError }: GetSageCountriesParams = {}) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.get("/sage-country", {
        params,
      });

      let result = responseDataFormate(response.data);

      if (result?.success) {
        onSuccess?.(result.data);
        return result.data;
      } else {
        onError?.();
      }
    } catch (error: any) {
      console.error("Get Sage Countries Error:", error);
      onError?.();
    }
  };

// --- Sage Taxes APIs ---

export interface GetSageTaxesParams {
  params?: any;
  onSuccess?: (data: any) => void;
  onError?: () => void;
}

export const getSageTaxesAPI =
  ({ params, onSuccess, onError }: GetSageTaxesParams = {}) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.get("/sage-taxes", {
        params,
      });

      let result = responseDataFormate(response.data);

      if (result?.success) {
        onSuccess?.(result.data);
        return result.data;
      } else {
        onError?.();
      }
    } catch (error: any) {
      console.error("Get Sage Taxes Error:", error);
      onError?.();
    }
  };

// --- Delivery Address APIs ---

export interface ClientDeliveryAddressFilterParams {
  customer_id: string;
  page?: number;
  limit?: number;
}

interface GetClientsDeliveryAddressListParams {
  filters: ClientDeliveryAddressFilterParams;
  onSuccess?: (data: any) => void;
  onError?: () => void;
}

export const getClientDeliveryAddressListAPI =
  ({ filters, onSuccess, onError }: GetClientsDeliveryAddressListParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.get(
        "/customers/delivery-addresses",
        {
          params: filters,
        },
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        onSuccess?.(result.data);
      } else {
        message.error(
          result?.message || "Failed to fetch Clients Delivery Address",
        );
        onError?.();
      }
    } catch (error: any) {
      console.error("Get Clients Delivery Address List Error:", error);
      message.error(
        error?.response?.data?.message ||
          "Unable to fetch Clients Delivery Address",
      );
      onError?.();
    }
  };

interface CreateDeliveryAddressParams {
  payload: any;
  params?: any;
  onSuccess: (data: any) => void;
  onError: () => void;
}

export const createClientDeliveryAddressAPI =
  ({ payload, params, onSuccess, onError }: CreateDeliveryAddressParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.post(
        `/customers/delivery-addresses`,
        payload,
        { params },
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(
          result?.message || "Delivery Address created successfully",
        );
        onSuccess?.(result.data);
      } else {
        message.error(result?.message || "Failed to create Delivery Address");
        onError?.();
      }
    } catch (error: any) {
      console.error("Create Delivery Address error:", error);
      onError?.();

      const errorMessage =
        error?.response?.data?.message || "Unable to create Delivery Address";
      message.error(errorMessage);
    }
  };

interface UpdateClientDeliveryAddressParams {
  payload: any;
  params?: any;
  onSuccess: (data: any) => void;
  onError: () => void;
}

export const updateClientDeliveryAddressAPI =
  ({ payload, params, onSuccess, onError }: UpdateClientDeliveryAddressParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.put(
        `/customers/delivery-addresses`,
        payload,
        { params },
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(
          result?.message || "Delivery Address updated successfully",
        );
        onSuccess?.(result.data);
      } else {
        message.error(result?.message || "Failed to update Delivery Address");
        onError?.();
      }
    } catch (error: any) {
      console.error("Update Delivery Address error:", error);
      onError?.();

      const errorMessage =
        error?.response?.data?.message || "Unable to update Delivery Address";
      message.error(errorMessage);
    }
  };

interface DeleteClientDeliveryAddressParams {
  id: string;
  params?: any;
  onSuccess: () => void;
  onError: () => void;
}

export const deleteClientDeliveryAddressAPI =
  ({ id, params, onSuccess, onError }: DeleteClientDeliveryAddressParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.delete(
        `/customers/delivery-addresses`,
        {
          data: { id: id },
          params,
        },
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(
          result?.message || "Delivery Address deleted successfully",
        );
        onSuccess?.();
      } else {
        message.error(result?.message || "Failed to delete Delivery Address");
        onError?.();
      }
    } catch (error: any) {
      console.error("Delete Delivery Address error:", error);
      onError?.();

      const errorMessage =
        error?.response?.data?.message || "Unable to delete Delivery Address";
      message.error(errorMessage);
    }
  };

interface CreateAdditionalContactsParams {
  payload: any;
  params?: any;
  onSuccess: (data: any) => void;
  onError: () => void;
}

export const createCustomerAdditionalContactsAPI =
  ({ payload, params, onSuccess, onError }: CreateAdditionalContactsParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.post(
        `/customers/additional-contact`,
        payload,
        { params },
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(
          result?.message || "Additional contacts created successfully",
        );
        onSuccess?.(result.data);
      } else {
        message.error(
          result?.message || "Failed to create additional contacts",
        );
        onError?.();
      }
    } catch (error: any) {
      console.error("Create additional contacts error:", error);
      onError?.();

      const errorMessage =
        error?.response?.data?.message ||
        "Unable to create additional contacts";
      message.error(errorMessage);
    }
  };

interface UpdateAdditionalContactParams {
  payload: any;
  params?: any;
  onSuccess: (data: any) => void;
  onError: () => void;
}

export const updateCustomerAdditionalContactAPI =
  ({ payload, params, onSuccess, onError }: UpdateAdditionalContactParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.put(
        `/customers/additional-contact`,
        payload,
        { params },
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(
          result?.message || "Additional contact updated successfully",
        );
        onSuccess?.(result.data);
      } else {
        message.error(result?.message || "Failed to update additional contact");
        onError?.();
      }
    } catch (error: any) {
      console.error("Update additional contact error:", error);
      onError?.();

      const errorMessage =
        error?.response?.data?.message || "Unable to update additional contact";
      message.error(errorMessage);
    }
  };

interface GetAdditionalContactsParams {
  id: string;
  onSuccess?: (data: any) => void;
  onError?: () => void;
}

export const getCustomerAdditionalContactsAPI =
  ({ id, onSuccess, onError }: GetAdditionalContactsParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.get(
        `/customers/additional-contact/${id}`,
      );
      let result = responseDataFormate(response.data);
      if (result?.success) {
        onSuccess?.(result.data);
      } else {
        message.error(result?.message || "Failed to fetch Additional Contacts");
        onError?.();
      }
    } catch (error: any) {
      console.error("Get Additional Contacts Error:", error);
      message.error(
        error?.response?.data?.message || "Unable to fetch Additional Contacts",
      );
      onError?.();
    }
  };

interface DeleteAdditionalContactParams {
  payload: any;
  params?: any;
  onSuccess: () => void;
  onError: () => void;
}

export const deleteCustomerAdditionalContactAPI =
  ({ payload, params, onSuccess, onError }: DeleteAdditionalContactParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.delete(
        `/customers/additional-contact`,
        { data: payload, params },
      );
      let result = responseDataFormate(response.data);
      if (result?.success) {
        message.success(
          result?.message || "Additional contact deleted successfully",
        );
        onSuccess?.();
      } else {
        message.error(result?.message || "Failed to delete additional contact");
        onError?.();
      }
    } catch (error: any) {
      console.error("Delete additional contact error:", error);
      onError?.();
      const errorMessage =
        error?.response?.data?.message || "Unable to delete additional contact";
      message.error(errorMessage);
    }
  };

interface SendCrendentialsParams {
  payload: any;
  params?: any;
  onSuccess: (data: any) => void;
  onError: () => void;
}

export const sendCrendentialsAPI =
  ({ payload, params, onSuccess, onError }: SendCrendentialsParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.post(
        `/customers/send-credentials`,
        payload,
        { params },
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message);
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      console.error("Send Crendential error:", error);
      onError?.();

      const errorMessage = error?.response?.data?.message;
      message.error(errorMessage);
    }
  };

interface GetClientsFiltersParams {
  onSuccess?: (data: any) => void;
  onError?: () => void;
}

export const getClientFiltersAPI =
  ({ onSuccess, onError }: GetClientsFiltersParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.get("/customers/filter-data");

      let result = responseDataFormate(response.data);

      if (result?.success) {
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      console.error("Get Clients Filters Error:", error);
      message.error(error?.response?.data?.message);
      onError?.();
    }
  };

interface GetDeliveryZonePriceParams {
  payload: {
    country_id: string;
    post_code: string | null;
    cross_border: boolean;
    area_id: string[] | null;
  };
  onSuccess?: (data: any) => void;
  onError?: () => void;
}

export const getDeliveryZonePriceByPostcodeAPI =
  ({ payload, onSuccess, onError }: GetDeliveryZonePriceParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.get(
        "/delivery-zone-price/by-post-code",
        { params: payload },
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        onSuccess?.(result.data);
      } else {
        onError?.();
      }
    } catch (error: any) {
      console.error("Get Delivery Zone Price Error:", error);
      onError?.();
    }
  };

interface GetDeliveryZoneForClientParams {
  country_id: string;
  search?: string;
  page?: number;
  limit?: number;
  onSuccess?: (data: any) => void;
  onError?: () => void;
}

export const getDeliveryZoneForClientAPI =
  ({ country_id, search = "", page = 1, limit = 25, onSuccess, onError }: GetDeliveryZoneForClientParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.get(
        "/delivery-postcode-prefix/all",
        {
          params: {
            country_id,
            search,
            page,
            limit,
          },
        },
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        onSuccess?.(result.data);
      } else {
        onError?.();
      }
    } catch (error: any) {
      console.error("Get Delivery Zone  Error:", error);
      onError?.();
    }
  };

  interface ToggleAdditionalContactStatusParams {
    payload: any;
    params?: any;
    onSuccess: (data: any) => void;
    onError: () => void;
  }

  export const toggleAdditionalContactStatusAPI =
    ({ payload, params, onSuccess, onError }: ToggleAdditionalContactStatusParams) =>
    async (dispatch: any) => {
      try {
        const response = await axiosInstance.put(
          `/customers/additional-contact/toggle-status`,
          payload,
          { params },
        );

        let result = responseDataFormate(response.data);

        if (result?.success) {
          message.success(
            result?.message || "Additional contact status updated successfully",
          );
          onSuccess?.(result.data);
        } else {
          message.error(
            result?.message || "Failed to update additional contact status",
          );
          onError?.();
        }
      } catch (error: any) {
        onError?.();

        const errorMessage =
          error?.response?.data?.message ||
          "Unable to update additional contact status";
        message.error(errorMessage);
      }
    };

interface ApproveCustomerParams {
  customerId: string;
  params?: any;
  onSuccess?: (data: any) => void;
  onError?: () => void;
}

export const approveCustomerAPI =
  ({ customerId, params, onSuccess, onError }: ApproveCustomerParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.put(
        `/customers/approve/${customerId}`,
        {},
        { params },
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message || "Customer approved successfully");
        onSuccess?.(result.data);
      } else {
        message.error(result?.message || "Failed to approve customer");
        onError?.();
      }
    } catch (error: any) {
      console.error("Approve Customer Error:", error);
      message.error(
        error?.response?.data?.message || "Unable to approve customer",
      );
      onError?.();
    }
  };

export const getUnapprovedCustomersCountAPI = () => async (dispatch: any) => {
  try {
    const response = await axiosInstance.get("/customers/unapproved-count");
    let result = responseDataFormate(response.data);
    if (result?.success) {
      console.log("Unapproved Customers Count Response:", result.data);
      return result.data?.count ?? result.data;
    }
    return 0;
  } catch (error: any) {
    console.error("Get Unapproved Customers Count Error:", error);
    return 0;
  }
};


